import { config } from "../../../config";

interface UserMarketingProfileOfferMetricsAddData {
  viewedAt?: Date; // aka PUO viewed
  clickedAt?: Date;
  convertedAt?: Date;
  personalUserOfferId: string;
  userMarketingProfileId: string;
  linkClicks?: number;
  websiteVisits?: number;
  pagesViewed?: number;
  sessionLengthSeconds?: number;
  conversionsTracked?: number;
  conversionValueCents?: number;
  isShadowProfile?: boolean;
}

interface ApiResponse {
  success: boolean;
  data?: any;
  error?: string;
  statusCode: number;
  responseText: string;
}

/**
 * Updates marketing metrics for a user marketing profile offer
 * @param data The metrics data to update
 * @returns Promise that resolves with the API response data
 */
export async function addUserMarketingProfileOfferMetrics(
  data: UserMarketingProfileOfferMetricsAddData
): Promise<ApiResponse> {
  // Development mode logging
  if (config.env === "development") {
    console.log("🎯 Add User Marketing Profile Offer Metrics Triggered:", {
      timestamp: new Date().toISOString(),
      data,
      endpoint: `${config.backend.base_url}/UserMarketingProfileOffer/AddUserMarketingProfileOffer`,
    });
  }

  try {
    const response = await fetch(
      `${config.backend.base_url}/UserMarketingProfileOffer/AddUserMarketingProfileOffer`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );

    // Get the response text
    const responseText = await response.text();

    // Try to parse as JSON if possible
    let responseData;
    try {
      responseData = responseText ? JSON.parse(responseText) : null;
    } catch (e) {
      responseData = null;
    }

    // Check if the response body contains an error type,
    // even if the HTTP status was successful
    const hasErrorInBody =
      responseData &&
      (responseData.type === "ERROR" || responseData.statusCode >= 400);

    const result: ApiResponse = {
      success: response.ok && !hasErrorInBody,
      statusCode: response.status,
      responseText: responseText,
      data: responseData,
    };

    if (!response.ok || hasErrorInBody) {
      result.error =
        responseData?.error?.reason ||
        `Failed to update metrics: ${response.statusText}`;
    }

    if (config.env === "development") {
      console.log("✅ User Marketing Profile Metrics Addition Response:", {
        timestamp: new Date().toISOString(),
        response: result,
        status: response.status,
        data,
      });
    }

    return result;
  } catch (error) {
    if (config.env === "development") {
      console.error("❌ User Marketing Profile Metrics Update Failed:", {
        timestamp: new Date().toISOString(),
        error,
        data,
      });
    } else {
      console.error("Error updating user marketing profile metrics:", error);
    }

    return {
      success: false,
      statusCode: 0,
      responseText: "",
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}
